import { Attachment } from "@/api/tickets";
import { AttachmentKind, attachmentIcon, attachmentKind } from "@/attachments/fileTypes";
import { convertToUnit, humanFileSize } from "@/utils";
import type { MarkdownIt, RendererRule } from "markdown-it";

/** The fields rendering needs; a structural subset so the portal payload fits too. */
export type MarkdownAttachment = Pick<
    Attachment,
    "fileName" | "fileSize" | "mimeType" | "url" | "thumbnailUrl"
> & { id: string };

export function attachmentImages(
    md: MarkdownIt,
    attachments: readonly MarkdownAttachment[],
    originalImage: RendererRule
) {
    const attachmentMap = new Map(attachments.map((a) => [a.fileName, a]));

    md.renderer.rules.image = function (tokens, idx, options, env, self): string {
        const token = tokens[idx];
        const attr = token?.attrGet("src")?.toString();

        if (!token || !attr) {
            return originalImage(tokens, idx, options, env, self);
        }

        const [src, args] = attr.split("?");
        if (!src) {
            throw new Error("This will never happen.");
        }

        const attachment = attachmentMap.get(decodeURIComponent(src).replaceAll("+", " "));
        if (!attachment) {
            return originalImage(tokens, idx, options, env, self);
        }

        const params = new URLSearchParams(args);

        const thumbnail = params.has("thumbnail");

        const width = convertToUnit(params.get("width"));
        const maxWidth = convertToUnit(params.get("max-width"));
        const height = convertToUnit(params.get("height"));
        const maxHeight = convertToUnit(params.get("max-height"));

        let style = "";
        if (width || maxWidth || height || maxHeight) {
            style = width ? `width: ${width};` : "";
            style += maxWidth ? `max-width: ${maxWidth};` : "";
            style += height ? `height: ${height};` : "";
            style += maxHeight ? `max-height: ${maxHeight};` : "";
        }

        const kind = attachmentKind(attachment);

        // An explicit ?thumbnail keeps meaning "the poster, linked", whatever the file is; only a
        // bare reference to a non-image becomes a player or a file card.
        if (!thumbnail && kind !== "image") {
            return renderMedia(attachment, kind, style);
        }

        if (thumbnail) token.attrJoin("class", "attachment-thumbnail");
        else {
            token.attrJoin("class", "attachment");
            token.attrJoin("data-attachment-id", attachment.id);
        }

        token.attrSet(
            "src",
            thumbnail && attachment.thumbnailUrl ? attachment.thumbnailUrl : attachment.url
        );

        if (style) {
            token.attrSet("style", style);
        }

        const img = originalImage(tokens, idx, options, env, self);

        if (thumbnail) {
            return `<a class="attachment" data-attachment-id="${attachment.id}" href="${attachment.url}">${img}</a>`;
        } else {
            return img;
        }
    };
}

function renderMedia(attachment: MarkdownAttachment, kind: AttachmentKind, style: string): string {
    const styleAttr = style ? ` style="${escapeHtml(style)}"` : "";
    const source = escapeHtml(attachment.url);

    if (kind === "video") {
        const poster = attachment.thumbnailUrl
            ? ` poster="${escapeHtml(attachment.thumbnailUrl)}"`
            : "";
        return `<video class="attachment-video" controls preload="metadata"${poster} src="${source}"${styleAttr}></video>`;
    }

    if (kind === "audio") {
        return `<audio class="attachment-audio" controls preload="metadata" src="${source}"${styleAttr}></audio>`;
    }

    const name = escapeHtml(attachment.fileName);
    const size = escapeHtml(humanFileSize(attachment.fileSize));
    const icon = attachmentIcon(attachment);
    const anchor = `class="attachment attachment-file" data-attachment-id="${attachment.id}" href="${source}"${styleAttr}`;

    if (attachment.thumbnailUrl) {
        return (
            `<a ${anchor}>` +
            `<span class="attachment-file__preview">` +
            `<img src="${escapeHtml(attachment.thumbnailUrl)}" alt="${name}" /></span>` +
            `<span class="attachment-file__caption"><i class="mdi ${icon}"></i>` +
            `<span class="attachment-file__name">${name}</span>` +
            `<span class="attachment-file__size">${size}</span></span></a>`
        );
    }

    return (
        `<a ${anchor}>` +
        `<i class="mdi ${icon} attachment-file__icon"></i>` +
        `<span class="attachment-file__name">${name}</span>` +
        `<span class="attachment-file__size">${size}</span></a>`
    );
}

function escapeHtml(value: string): string {
    return value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");
}

export default attachmentImages;
